/**
 * Forms for the scenario-wide sections: overview, roles, scoring (dimensions and variables), injects
 * and badges. Ids here are the ones nodes point at, so each goes through `IdField` and a rename
 * rewrites every reference.
 */
import type { Badge, BadgeCondition, Scenario } from "@shared/scenario";
import { hasIcon, Icon } from "../../components/Icon";
import { Button, Segmented } from "../../components/ui";
import { useT } from "../../i18n";
import {
  BField,
  FormGroup,
  fid,
  IdField,
  NumberField,
  RowTools,
  SelectField,
  StringList,
  TextArea,
  TextField,
} from "./fields";
import { countRefs, freshId, IDENT, KEBAB, move, renameId, type SectionId } from "./model";
import type { Edit } from "./NodeForm";

export function SectionForm({
  section,
  scenario,
  edit,
}: {
  section: SectionId;
  scenario: Scenario;
  edit: Edit;
}) {
  switch (section) {
    case "overview":
      return <OverviewForm s={scenario} edit={edit} />;
    case "roles":
      return <RolesForm s={scenario} edit={edit} />;
    case "scoring":
      return <ScoringForm s={scenario} edit={edit} />;
    case "injects":
      return <InjectsForm s={scenario} edit={edit} />;
    case "badges":
      return <BadgesForm s={scenario} edit={edit} />;
  }
}

function SectionHead({ title, lead }: { title: string; lead: string }) {
  return (
    <div class="build-form-head">
      <div class="build-form-title">
        <h2>{title}</h2>
      </div>
      <p class="muted small build-lead">{lead}</p>
    </div>
  );
}

/** A text input for a Lucide icon name with the icon itself beside it. */
function IconField({
  label,
  path,
  value,
  fallback,
  onChange,
}: {
  label: string;
  path: string;
  value: string;
  fallback: string;
  onChange: (v: string) => void;
}) {
  const t = useT();
  const known = hasIcon(value);
  const id = fid(path);
  return (
    <BField
      label={label}
      path={path}
      help={known ? t("build.help.icon") : t("build.help.iconUnknown", { fallback })}
    >
      <div class="build-icon-row">
        <input
          id={id}
          class="input build-mono"
          value={value}
          spellcheck={false}
          autocomplete="off"
          aria-describedby={`${id}-help`}
          onInput={(e) => onChange((e.currentTarget as HTMLInputElement).value)}
        />
        <span class="build-icon-preview" aria-hidden="true">
          <Icon name={known ? value : fallback} />
        </span>
      </div>
    </BField>
  );
}

function OverviewForm({ s, edit }: { s: Scenario; edit: Edit }) {
  const t = useT();
  const nodeOptions = Object.entries(s.nodes).map(([id, n]) => ({ value: id, label: `${id} · ${n.title}` }));
  return (
    <div class="stack build-form-body">
      <SectionHead title={t("build.section.overview")} lead={t("build.lead.overview")} />
      <FormGroup title={t("build.group.catalogue")}>
        <TextField
          label={t("build.field.title")}
          path="title"
          value={s.title}
          help={t("build.help.title")}
          onChange={(v) =>
            edit((x) => {
              x.title = v;
            })
          }
        />
        <TextField
          label={t("build.field.tagline")}
          path="tagline"
          value={s.tagline}
          help={t("build.help.tagline")}
          onChange={(v) =>
            edit((x) => {
              x.tagline = v;
            })
          }
        />
        <TextArea
          label={t("build.field.summary")}
          path="summary"
          value={s.summary}
          rows={4}
          help={t("build.help.summary")}
          onChange={(v) =>
            edit((x) => {
              x.summary = v;
            })
          }
        />
        <div data-path="difficulty">
          <Segmented
            label={t("build.field.difficulty")}
            value={s.difficulty}
            options={(["intro", "standard", "advanced"] as const).map((d) => ({
              value: d,
              label: t(`scenario.difficulty.${d}`),
            }))}
            onChange={(v) =>
              edit((x) => {
                x.difficulty = v;
              })
            }
          />
        </div>
        <div class="build-pair">
          <NumberField
            label={t("build.field.minutes")}
            path="estimatedMinutes"
            value={s.estimatedMinutes}
            min={10}
            max={240}
            step={5}
            help={t("build.help.minutes")}
            onChange={(v) =>
              edit((x) => {
                if (v !== undefined) x.estimatedMinutes = v;
              })
            }
          />
          <TextField
            label={t("build.field.version")}
            path="version"
            value={s.version}
            mono
            help={t("build.help.version")}
            onChange={(v) =>
              edit((x) => {
                x.version = v;
              })
            }
          />
        </div>
      </FormGroup>
      <FormGroup title={t("build.group.audience")}>
        <SelectField
          label={t("build.field.audience")}
          path="audience"
          value={s.audience}
          options={(["workplace", "community", "youth"] as const).map((a) => ({
            value: a,
            label: t(`audience.${a}`),
          }))}
          help={t("build.help.audience")}
          onChange={(v) =>
            edit((x) => {
              x.audience = v;
            })
          }
        />
        <TextField
          label={t("build.field.audienceNote")}
          path="audienceNote"
          value={s.audienceNote ?? ""}
          help={t("build.help.audienceNote")}
          onChange={(v) =>
            edit((x) => {
              if (v) x.audienceNote = v;
              else delete x.audienceNote;
            })
          }
        />
      </FormGroup>
      <FormGroup title={t("build.group.teaching")}>
        <StringList
          label={t("build.field.objectives")}
          path="learningObjectives"
          items={s.learningObjectives}
          itemLabel={t("build.item.objective")}
          multiline
          help={t("build.help.objectives")}
          onChange={(v) =>
            edit((x) => {
              x.learningObjectives = v;
            })
          }
        />
        <StringList
          label={t("build.field.tags")}
          path="tags"
          items={s.tags}
          itemLabel={t("build.item.tag")}
          help={t("build.help.tags")}
          onChange={(v) =>
            edit((x) => {
              x.tags = v;
            })
          }
        />
        <SelectField
          label={t("build.field.start")}
          path="start"
          value={s.start}
          options={nodeOptions}
          help={t("build.help.start")}
          onChange={(v) =>
            edit((x) => {
              x.start = v;
            })
          }
        />
      </FormGroup>
      <FormGroup
        title={t("build.group.sources")}
        path="basedOn"
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="plus"
            onClick={() =>
              edit((x) => {
                x.basedOn.push({ name: "", year: new Date().getFullYear() });
              })
            }
          >
            {t("build.source.add")}
          </Button>
        }
      >
        <p class="muted small">{t("build.help.basedOn")}</p>
        {s.basedOn.length ? (
          <ol class="build-cards">
            {s.basedOn.map((b, i) => {
              const p = `basedOn.${i}`;
              const rowLabel = t("build.source.n", { n: i + 1 });
              return (
                <li key={i} class="build-card" data-path={p}>
                  <div class="build-card-head">
                    <span class="eyebrow">{rowLabel}</span>
                    <RowTools
                      label={rowLabel}
                      index={i}
                      count={s.basedOn.length}
                      onMove={(d) =>
                        edit((x) => {
                          x.basedOn = move(x.basedOn, i, d);
                        })
                      }
                      onRemove={() =>
                        edit((x) => {
                          x.basedOn.splice(i, 1);
                        })
                      }
                    />
                  </div>
                  <TextField
                    label={t("build.field.sourceName")}
                    path={`${p}.name`}
                    value={b.name}
                    onChange={(v) =>
                      edit((x) => {
                        const it = x.basedOn[i];
                        if (it) it.name = v;
                      })
                    }
                  />
                  <div class="build-pair">
                    <NumberField
                      label={t("build.field.sourceYear")}
                      path={`${p}.year`}
                      value={b.year}
                      step={1}
                      onChange={(v) =>
                        edit((x) => {
                          const it = x.basedOn[i];
                          if (it && v !== undefined) it.year = Math.trunc(v);
                        })
                      }
                    />
                    <TextField
                      label={t("build.field.sourceUrl")}
                      path={`${p}.url`}
                      type="url"
                      value={b.url ?? ""}
                      onChange={(v) =>
                        edit((x) => {
                          const it = x.basedOn[i];
                          if (!it) return;
                          if (v) it.url = v;
                          else delete it.url;
                        })
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        ) : null}
      </FormGroup>
    </div>
  );
}

function RolesForm({ s, edit }: { s: Scenario; edit: Edit }) {
  const t = useT();
  return (
    <div class="stack build-form-body">
      <SectionHead title={t("build.section.roles")} lead={t("build.lead.roles")} />
      <ol class="build-cards" data-path="roles">
        {s.roles.map((r, i) => {
          const p = `roles.${i}`;
          const rowLabel = r.name || r.id;
          return (
            <li key={`${i}-${r.id}`} class="build-card" data-path={p}>
              <div class="build-card-head">
                <span class="build-card-title">
                  <Icon name={hasIcon(r.icon) ? r.icon : "user"} />
                  <strong>{rowLabel}</strong>
                </span>
                <RowTools
                  label={rowLabel}
                  index={i}
                  count={s.roles.length}
                  min={2}
                  onMove={(d) =>
                    edit((x) => {
                      x.roles = move(x.roles, i, d);
                    })
                  }
                  onRemove={() =>
                    edit((x) => {
                      x.roles.splice(i, 1);
                    })
                  }
                />
              </div>
              <div class="build-pair">
                <IdField
                  label={t("build.field.roleId")}
                  path={`${p}.id`}
                  value={r.id}
                  taken={s.roles.map((x) => x.id)}
                  pattern={KEBAB}
                  refs={countRefs(s, "role", r.id)}
                  onRename={(to) => edit((x) => renameId(x, "role", r.id, to))}
                />
                <TextField
                  label={t("build.field.roleName")}
                  path={`${p}.name`}
                  value={r.name}
                  onChange={(v) =>
                    edit((x) => {
                      const it = x.roles[i];
                      if (it) it.name = v;
                    })
                  }
                />
              </div>
              <div class="build-pair">
                <IconField
                  label={t("build.field.icon")}
                  path={`${p}.icon`}
                  value={r.icon}
                  fallback="user"
                  onChange={(v) =>
                    edit((x) => {
                      const it = x.roles[i];
                      if (it) it.icon = v;
                    })
                  }
                />
                <NumberField
                  label={t("build.field.maxPlayers")}
                  path={`${p}.maxPlayers`}
                  value={r.maxPlayers}
                  min={1}
                  optional
                  help={t("build.help.maxPlayers")}
                  onChange={(v) =>
                    edit((x) => {
                      const it = x.roles[i];
                      if (!it) return;
                      if (v === undefined) delete it.maxPlayers;
                      else it.maxPlayers = Math.max(1, Math.trunc(v));
                    })
                  }
                />
              </div>
              <TextArea
                label={t("build.field.brief")}
                path={`${p}.brief`}
                value={r.brief}
                rows={3}
                help={t("build.help.brief")}
                onChange={(v) =>
                  edit((x) => {
                    const it = x.roles[i];
                    if (it) it.brief = v;
                  })
                }
              />
              <StringList
                label={t("build.field.roleObjectives")}
                path={`${p}.objectives`}
                items={r.objectives}
                itemLabel={t("build.item.objective")}
                onChange={(v) =>
                  edit((x) => {
                    const it = x.roles[i];
                    if (it) it.objectives = v;
                  })
                }
              />
            </li>
          );
        })}
      </ol>
      <div>
        <Button
          variant="secondary"
          icon="plus"
          onClick={() =>
            edit((x) => {
              x.roles.push({
                id: freshId(
                  "role",
                  x.roles.map((r) => r.id),
                ),
                name: "New role",
                icon: "user",
                brief: "What this player knows and is responsible for.",
                objectives: [],
              });
            })
          }
        >
          {t("build.role.add")}
        </Button>
      </div>
    </div>
  );
}

function ScoringForm({ s, edit }: { s: Scenario; edit: Edit }) {
  const t = useT();
  const vars = Object.entries(s.variables);
  return (
    <div class="stack build-form-body">
      <SectionHead title={t("build.section.scoring")} lead={t("build.lead.scoring")} />
      <FormGroup
        title={t("build.group.dimensions")}
        path="dimensions"
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="plus"
            disabled={s.dimensions.length >= 8}
            onClick={() =>
              edit((x) => {
                x.dimensions.push({
                  id: freshId(
                    "dimension",
                    x.dimensions.map((d) => d.id),
                  ),
                  label: "New dimension",
                  description: "What a room that scores well here did.",
                });
              })
            }
          >
            {t("build.dimension.add")}
          </Button>
        }
      >
        <p class="muted small">{t("build.help.dimensions")}</p>
        <ol class="build-cards">
          {s.dimensions.map((d, i) => {
            const p = `dimensions.${i}`;
            const rowLabel = d.label || d.id;
            return (
              <li key={`${i}-${d.id}`} class="build-card" data-path={`dimensions.${d.id}`}>
                <div class="build-card-head">
                  <strong class="build-card-title">{rowLabel}</strong>
                  <RowTools
                    label={rowLabel}
                    index={i}
                    count={s.dimensions.length}
                    min={2}
                    onMove={(delta) =>
                      edit((x) => {
                        x.dimensions = move(x.dimensions, i, delta);
                      })
                    }
                    onRemove={() =>
                      edit((x) => {
                        x.dimensions.splice(i, 1);
                      })
                    }
                  />
                </div>
                <div class="build-pair">
                  <IdField
                    label={t("build.field.dimensionId")}
                    path={`${p}.id`}
                    value={d.id}
                    taken={s.dimensions.map((x) => x.id)}
                    pattern={KEBAB}
                    refs={countRefs(s, "dimension", d.id)}
                    onRename={(to) => edit((x) => renameId(x, "dimension", d.id, to))}
                  />
                  <TextField
                    label={t("build.field.dimensionLabel")}
                    path={`${p}.label`}
                    value={d.label}
                    onChange={(v) =>
                      edit((x) => {
                        const it = x.dimensions[i];
                        if (it) it.label = v;
                      })
                    }
                  />
                </div>
                <TextField
                  label={t("build.field.description")}
                  path={`${p}.description`}
                  value={d.description}
                  onChange={(v) =>
                    edit((x) => {
                      const it = x.dimensions[i];
                      if (it) it.description = v;
                    })
                  }
                />
              </li>
            );
          })}
        </ol>
      </FormGroup>
      <FormGroup
        title={t("build.group.variables")}
        path="variables"
        actions={
          <Button
            variant="secondary"
            size="sm"
            icon="plus"
            onClick={() =>
              edit((x) => {
                let n = 1;
                while (`var${n}` in x.variables) n++;
                x.variables[`var${n}`] = 0;
              })
            }
          >
            {t("build.variable.add")}
          </Button>
        }
      >
        <p class="muted small">{t("build.help.variables")}</p>
        {vars.length ? (
          <ol class="build-cards">
            {vars.map(([name, start], i) => {
              const rowLabel = name;
              return (
                <li key={`${i}-${name}`} class="build-card" data-path={`variables.${name}`}>
                  <div class="build-var-row">
                    <IdField
                      label={t("build.field.variableId")}
                      path={`variables.${name}.id`}
                      value={name}
                      taken={vars.map(([k]) => k)}
                      pattern={IDENT}
                      refs={countRefs(s, "variable", name)}
                      onRename={(to) => edit((x) => renameId(x, "variable", name, to))}
                    />
                    <NumberField
                      label={t("build.field.variableStart")}
                      path={`variables.${name}`}
                      value={start}
                      onChange={(v) =>
                        edit((x) => {
                          if (v !== undefined) x.variables[name] = v;
                        })
                      }
                    />
                    <Button
                      variant="ghost"
                      size="sm"
                      icon="trash"
                      class="build-var-remove"
                      aria-label={t("build.list.remove", { item: rowLabel })}
                      title={t("build.list.remove", { item: rowLabel })}
                      onClick={() =>
                        edit((x) => {
                          delete x.variables[name];
                        })
                      }
                    />
                  </div>
                </li>
              );
            })}
          </ol>
        ) : (
          <p class="muted small build-empty-line">{t("build.list.none")}</p>
        )}
      </FormGroup>
    </div>
  );
}

function InjectsForm({ s, edit }: { s: Scenario; edit: Edit }) {
  const t = useT();
  const nodeOptions = Object.entries(s.nodes).map(([id, n]) => ({ value: id, label: `${id} · ${n.title}` }));
  return (
    <div class="stack build-form-body">
      <SectionHead title={t("build.section.injects")} lead={t("build.lead.injects")} />
      <ol class="build-cards" data-path="injects">
        {s.injects.map((inj, i) => {
          const p = `injects.${i}`;
          const rowLabel = inj.title || inj.id;
          return (
            <li key={`${i}-${inj.id}`} class="build-card" data-path={`injects.${inj.id}`}>
              <div class="build-card-head">
                <strong class="build-card-title">{rowLabel}</strong>
                <RowTools
                  label={rowLabel}
                  index={i}
                  count={s.injects.length}
                  onMove={(d) =>
                    edit((x) => {
                      x.injects = move(x.injects, i, d);
                    })
                  }
                  onRemove={() =>
                    edit((x) => {
                      x.injects.splice(i, 1);
                    })
                  }
                />
              </div>
              <div class="build-pair">
                <IdField
                  label={t("build.field.injectId")}
                  path={`${p}.id`}
                  value={inj.id}
                  taken={s.injects.map((x) => x.id)}
                  pattern={KEBAB}
                  onRename={(to) =>
                    edit((x) => {
                      const it = x.injects[i];
                      if (it) it.id = to;
                    })
                  }
                />
                <TextField
                  label={t("build.field.title")}
                  path={`${p}.title`}
                  value={inj.title}
                  onChange={(v) =>
                    edit((x) => {
                      const it = x.injects[i];
                      if (it) it.title = v;
                    })
                  }
                />
              </div>
              <TextArea
                label={t("build.field.description")}
                path={`${p}.description`}
                value={inj.description}
                rows={2}
                help={t("build.help.injectDescription")}
                onChange={(v) =>
                  edit((x) => {
                    const it = x.injects[i];
                    if (it) it.description = v;
                  })
                }
              />
              <SelectField
                label={t("build.field.injectNode")}
                path={`${p}.node`}
                value={inj.node}
                options={nodeOptions}
                help={t("build.help.injectNode")}
                onChange={(v) =>
                  edit((x) => {
                    const it = x.injects[i];
                    if (it) it.node = v;
                  })
                }
              />
              <AvailableAfter
                label={t("build.field.availableAfter")}
                path={`${p}.availableAfter`}
                value={inj.availableAfter}
                options={nodeOptions}
                onChange={(v) =>
                  edit((x) => {
                    const it = x.injects[i];
                    if (it) it.availableAfter = v;
                  })
                }
              />
            </li>
          );
        })}
      </ol>
      {s.injects.length ? null : <p class="muted small build-empty-line">{t("build.inject.empty")}</p>}
      <div>
        <Button
          variant="secondary"
          icon="plus"
          onClick={() =>
            edit((x) => {
              x.injects.push({
                id: freshId(
                  "inject",
                  x.injects.map((it) => it.id),
                ),
                title: "New inject",
                description: "What changes when the facilitator throws this in.",
                node: x.start,
                availableAfter: [],
              });
            })
          }
        >
          {t("build.inject.add")}
        </Button>
      </div>
    </div>
  );
}

/** A list of node selects; empty means the inject is always on offer. */
function AvailableAfter({
  label,
  path,
  value,
  options,
  onChange,
}: {
  label: string;
  path: string;
  value: string[];
  options: { value: string; label: string }[];
  onChange: (v: string[]) => void;
}) {
  const t = useT();
  return (
    <div class="field" data-path={path}>
      <div class="label">{label}</div>
      {value.map((v, i) => (
        <div key={i} class="build-list-row">
          <select
            class="input"
            value={v}
            aria-label={`${label} ${i + 1}`}
            onChange={(e) =>
              onChange(value.map((x, j) => (j === i ? (e.currentTarget as HTMLSelectElement).value : x)))
            }
          >
            {options.some((o) => o.value === v) ? null : <option value={v}>{v}</option>}
            {options.map((o) => (
              <option key={o.value} value={o.value}>
                {o.label}
              </option>
            ))}
          </select>
          <Button
            variant="ghost"
            size="sm"
            icon="trash"
            aria-label={t("build.list.remove", { item: `${label} ${i + 1}` })}
            title={t("build.list.remove", { item: `${label} ${i + 1}` })}
            onClick={() => onChange(value.filter((_, j) => j !== i))}
          />
        </div>
      ))}
      <div class="help">
        {value.length ? t("build.help.availableAfter") : t("build.help.availableAlways")}
      </div>
      <div>
        <Button
          variant="secondary"
          size="sm"
          icon="plus"
          onClick={() => onChange([...value, options[0]?.value ?? ""])}
        >
          {t("build.inject.addAfter")}
        </Button>
      </div>
    </div>
  );
}

const CONDITION_KINDS: BadgeCondition["kind"][] = [
  "optionTag",
  "speed",
  "consensus",
  "bestPicks",
  "teamVarBelow",
  "teamDimension",
];

function defaultCondition(kind: BadgeCondition["kind"], s: Scenario): BadgeCondition {
  switch (kind) {
    case "optionTag":
      return { kind, tag: "verify", min: 2 };
    case "speed":
      return { kind, fraction: 0.5, minVotes: 3 };
    case "consensus":
      return { kind, fraction: 0.75, minVotes: 3 };
    case "bestPicks":
      return { kind, min: 3 };
    case "teamVarBelow":
      return { kind, var: Object.keys(s.variables)[0] ?? "", below: 50 };
    case "teamDimension":
      return { kind, dimension: s.dimensions[0]?.id ?? "", min: 80 };
  }
}

function BadgesForm({ s, edit }: { s: Scenario; edit: Edit }) {
  const t = useT();
  return (
    <div class="stack build-form-body">
      <SectionHead title={t("build.section.badges")} lead={t("build.lead.badges")} />
      <ol class="build-cards" data-path="badges">
        {s.badges.map((b, i) => {
          const p = `badges.${i}`;
          const rowLabel = b.name || b.id;
          const setBadge = (fn: (x: Badge) => void) =>
            edit((x) => {
              const it = x.badges[i];
              if (it) fn(it);
            });
          return (
            <li key={`${i}-${b.id}`} class="build-card" data-path={`badges.${b.id}`}>
              <div class="build-card-head">
                <span class="build-card-title">
                  <Icon name={hasIcon(b.icon) ? b.icon : "award"} />
                  <strong>{rowLabel}</strong>
                </span>
                <RowTools
                  label={rowLabel}
                  index={i}
                  count={s.badges.length}
                  onMove={(d) =>
                    edit((x) => {
                      x.badges = move(x.badges, i, d);
                    })
                  }
                  onRemove={() =>
                    edit((x) => {
                      x.badges.splice(i, 1);
                    })
                  }
                />
              </div>
              <div class="build-pair">
                <IdField
                  label={t("build.field.badgeId")}
                  path={`${p}.id`}
                  value={b.id}
                  taken={s.badges.map((x) => x.id)}
                  pattern={KEBAB}
                  onRename={(to) =>
                    setBadge((x) => {
                      x.id = to;
                    })
                  }
                />
                <TextField
                  label={t("build.field.badgeName")}
                  path={`${p}.name`}
                  value={b.name}
                  onChange={(v) =>
                    setBadge((x) => {
                      x.name = v;
                    })
                  }
                />
              </div>
              <TextField
                label={t("build.field.description")}
                path={`${p}.description`}
                value={b.description}
                onChange={(v) =>
                  setBadge((x) => {
                    x.description = v;
                  })
                }
              />
              <IconField
                label={t("build.field.icon")}
                path={`${p}.icon`}
                value={b.icon}
                fallback="award"
                onChange={(v) =>
                  setBadge((x) => {
                    x.icon = v;
                  })
                }
              />
              <SelectField
                label={t("build.field.condition")}
                path={`${p}.condition.kind`}
                value={b.condition.kind}
                options={CONDITION_KINDS.map((k) => ({ value: k, label: t(`build.badge.kind.${k}`) }))}
                help={t(`build.badge.help.${b.condition.kind}`)}
                onChange={(v) =>
                  setBadge((x) => {
                    x.condition = defaultCondition(v, s);
                  })
                }
              />
              <ConditionFields
                s={s}
                path={`${p}.condition`}
                c={b.condition}
                onChange={(c) =>
                  setBadge((x) => {
                    x.condition = c;
                  })
                }
              />
            </li>
          );
        })}
      </ol>
      {s.badges.length ? null : <p class="muted small build-empty-line">{t("build.badge.empty")}</p>}
      <div>
        <Button
          variant="secondary"
          icon="plus"
          onClick={() =>
            edit((x) => {
              x.badges.push({
                id: freshId(
                  "badge",
                  x.badges.map((b) => b.id),
                ),
                name: "New badge",
                description: "What the player did to earn it.",
                icon: "award",
                condition: { kind: "bestPicks", min: 3 },
              });
            })
          }
        >
          {t("build.badge.add")}
        </Button>
      </div>
    </div>
  );
}

function ConditionFields({
  s,
  path,
  c,
  onChange,
}: {
  s: Scenario;
  path: string;
  c: BadgeCondition;
  onChange: (c: BadgeCondition) => void;
}) {
  const t = useT();
  const num = (
    key: string,
    label: string,
    value: number,
    set: (v: number) => BadgeCondition,
    opts: { min?: number; max?: number; step?: number } = {},
  ) => (
    <NumberField
      key={key}
      label={label}
      path={`${path}.${key}`}
      value={value}
      min={opts.min}
      max={opts.max}
      step={opts.step ?? 1}
      onChange={(v) => {
        if (v !== undefined) onChange(set(v));
      }}
    />
  );
  switch (c.kind) {
    case "optionTag":
      return (
        <div class="build-pair">
          <TextField
            label={t("build.field.tag")}
            path={`${path}.tag`}
            value={c.tag}
            mono
            onChange={(v) => onChange({ ...c, tag: v })}
          />
          {num("min", t("build.field.minTimes"), c.min, (v) => ({ ...c, min: Math.max(1, Math.trunc(v)) }), {
            min: 1,
          })}
        </div>
      );
    case "speed":
    case "consensus":
      return (
        <div class="build-pair">
          {num("fraction", t("build.field.fraction"), c.fraction, (v) => ({ ...c, fraction: v }), {
            min: 0,
            max: 1,
            step: 0.05,
          })}
          {num(
            "minVotes",
            t("build.field.minVotes"),
            c.minVotes,
            (v) => ({ ...c, minVotes: Math.max(1, Math.trunc(v)) }),
            { min: 1 },
          )}
        </div>
      );
    case "bestPicks":
      return num(
        "min",
        t("build.field.minTimes"),
        c.min,
        (v) => ({ ...c, min: Math.max(1, Math.trunc(v)) }),
        {
          min: 1,
        },
      );
    case "teamVarBelow":
      return (
        <div class="build-pair">
          <SelectField
            label={t("build.field.var")}
            path={`${path}.var`}
            value={c.var}
            options={Object.keys(s.variables).map((v) => ({ value: v, label: v }))}
            onChange={(v) => onChange({ ...c, var: v })}
          />
          {num("below", t("build.field.below"), c.below, (v) => ({ ...c, below: v }))}
        </div>
      );
    case "teamDimension":
      return (
        <div class="build-pair">
          <SelectField
            label={t("build.field.dimension")}
            path={`${path}.dimension`}
            value={c.dimension}
            options={s.dimensions.map((d) => ({ value: d.id, label: d.label }))}
            onChange={(v) => onChange({ ...c, dimension: v })}
          />
          {num("min", t("build.field.minPercent"), c.min, (v) => ({ ...c, min: v }), { min: 0, max: 100 })}
        </div>
      );
  }
}
